/**
 * Başlıkta her sekmede görünen FOMC özeti: bir sonraki toplantı, artırım/indirim olasılığı,
 * önceki uzlaşmaya (1G) ve bir hafta öncesine (1H) göre değişim. Tıklanınca Makro sekmesi açılır.
 */
import { Landmark } from 'lucide-react';
import { useFedSnapshot, fmtMeeting, fmtTsi } from '../lib/fedwatch';

function Delta({ v, label }: { v: number | null; label: string }) {
  if (v === null || v === undefined) return null;
  const up = v > 0, flat = Math.abs(v) < 0.5;
  // ▲ = daha fazla sıkılaştırma fiyatlanıyor (risk varlıkları için olumsuz yön)
  const cls = flat ? 'text-blue-600' : up ? 'text-amber-400' : 'text-emerald-400';
  return (
    <span className={`font-mono tabular-nums ${cls}`}>
      {label} {flat ? '–' : `${up ? '▲' : '▼'}${Math.abs(v).toFixed(0)}`}
    </span>
  );
}

export default function FedChip({ onOpen }: { onOpen: () => void }) {
  const snap = useFedSnapshot();
  const m = snap?.ready ? snap.meetings?.[0] : undefined;

  if (!snap?.ready || !m) {
    return (
      <button onClick={onOpen} title="Fed fiyatlaması — veri bekleniyor"
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-[#061530] border border-[#0a2560] text-[10px] text-blue-700 whitespace-nowrap">
        <Landmark size={12} /> FOMC —
      </button>
    );
  }

  const cut = m.p_cut > m.p_hike;
  const p = (cut ? m.p_cut : m.p_hike) * 100;
  const when = snap.ts ? fmtTsi(snap.ts) : '';
  const title = [
    `FOMC ${fmtMeeting(m.meeting)} — ${cut ? 'indirim' : 'artırım'} olasılığı %${p.toFixed(0)}`,
    `Kaynak: ${m.contract} (${m.method === 'anchor' ? 'çapa ay' : 'kendi ayından çözüm'}), ±${m.precision_pp} puan/tik`,
    `Son ölçüm: ${when} TSİ (${snap.kind === 'settle' ? 'uzlaşma' : 'seans içi'})`,
    snap.stale ? 'UYARI: veri 30 dakikadan eski' : '',
    snap.r0_stale ? 'UYARI: EFFR son kararı henüz yansıtmıyor' : '',
  ].filter(Boolean).join('\n');

  return (
    <button onClick={onOpen} title={title}
      className={`inline-flex items-center gap-2 px-2.5 py-1 rounded-lg bg-[#061530] border border-[#0a2560] text-[11px] whitespace-nowrap hover:border-blue-500 transition-colors ${snap.stale ? 'opacity-50' : ''}`}>
      <Landmark size={12} className="text-blue-400" />
      <span className="text-blue-400 font-semibold">FOMC {fmtMeeting(m.meeting, false)}</span>
      <span className="text-white font-bold font-mono tabular-nums">
        {cut ? 'indirim' : 'artırım'} %{p.toFixed(0)}
      </span>
      <Delta v={m.d1} label="1G" />
      <Delta v={m.d7} label="1H" />
      {snap.r0_stale && <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />}
    </button>
  );
}
